import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractCollection,
  extractPayload,
} from '@/utils/dataNormalization';

export const createCustomerDetailSaleActionState = () => ({
  showSaleEditModal: false,
  saleModalMode: 'edit',
  saleModalInitialData: null,
  editingSaleId: 0,
  saleSubmitting: false,
  isStatusModalOpen: false,
  statusModalSubmitting: false,
  selectedSaleForStatus: null,
  statusOptions: [],
  isDeleteConfirmModalOpen: false,
  deletingSale: null,
  deletingSaleSource: 'table',
});

export const createCustomerDetailSaleActionComputed = ({ entityLabel = 'Negocio' } = {}) => ({
  isAdvertisingModuleActive() {
    const can = typeof this.$can === 'function' ? this.$can : null;
    if (!can) {
      return true;
    }

    return can('sales.advertising');
  },
  deleteConfirmMessage() {
    const saleName = String(this.deletingSale?.sale_name || this.deletingSale?.name || '').trim();
    const saleLabel = saleName || 'Sin nombre';

    return `${entityLabel}: ${saleLabel}\n¿Deseas continuar con la eliminación?`;
  },
});

export const createCustomerDetailSaleActionMethods = ({
  readService,
  writeService,
  buildUpdateRequestData,
  messages = {},
} = {}) => ({
  async handleChangeSaleStatus(row) {
    const saleId = Number(row?.id || 0);
    if (saleId <= 0) {
      return;
    }

    this.selectedSaleForStatus = row;
    this.isStatusModalOpen = true;
    await this.loadStatusOptions();
  },
  closeStatusModal(force = false) {
    if (!force && this.statusModalSubmitting) {
      return;
    }

    this.isStatusModalOpen = false;
    this.selectedSaleForStatus = null;
  },
  resolveSelectedSaleName() {
    return String(this.selectedSaleForStatus?.sale_name || this.selectedSaleForStatus?.name || '').trim();
  },
  resolveSelectedSaleStatusLabel() {
    return String(this.selectedSaleForStatus?.status_name || this.selectedSaleForStatus?.status || '').trim();
  },
  resolveSelectedSaleStatusId() {
    return Number(this.selectedSaleForStatus?.status_sale_id || 0);
  },
  async loadStatusOptions() {
    if (this.statusOptions.length > 0) {
      return;
    }

    try {
      const response = await writeService.statuses({}, { skipLoading: true });
      this.statusOptions = normalizeCatalogOptions(extractCollection(response), { useCodeInLabel: false });
    } catch (error) {
      this.statusOptions = [];
      this.$toast?.error(extractApiErrorMessage(error, messages.loadStatusOptionsError || 'No fue posible cargar los estados de negocio.'));
    }
  },
  async confirmUpdateSaleStatus(statusSaleId) {
    const saleId = Number(this.selectedSaleForStatus?.id || 0);
    const nextStatusId = Number(statusSaleId || 0);
    if (saleId <= 0 || nextStatusId <= 0) {
      return;
    }

    this.statusModalSubmitting = true;

    try {
      const response = await writeService.updateStatus(saleId, {
        status_sale_id: nextStatusId,
      });

      const apiError = extractApiErrorMessage(response, messages.updateStatusError || 'No fue posible actualizar el estado del negocio.');
      if (apiError) {
        this.$toast?.error(apiError);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, messages.updateStatusSuccess || 'El estado del negocio ha sido actualizado correctamente.'));
      this.closeStatusModal(true);
      await this.fetchSales(true);
    } catch (error) {
      this.$toast?.error(extractApiErrorMessage(error, messages.updateStatusError || 'No fue posible actualizar el estado del negocio.'));
    } finally {
      this.statusModalSubmitting = false;
    }
  },
  closeSaleModal(force = false) {
    if (!force && this.saleSubmitting) {
      return;
    }

    this.showSaleEditModal = false;

    if (force) {
      this.saleModalMode = 'edit';
      this.saleModalInitialData = null;
      this.editingSaleId = 0;
    }
  },
  async submitSaleEdit(payload) {
    const saleId = Number(this.editingSaleId || 0);
    if (saleId <= 0) {
      return;
    }

    this.saleSubmitting = true;

    try {
      const requestData = buildUpdateRequestData(payload);
      const response = await writeService.update(saleId, requestData);

      const apiError = extractApiErrorMessage(response, messages.updateError || 'No fue posible actualizar el negocio.');
      if (apiError) {
        this.$toast?.error(apiError);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, messages.updateSuccess || 'El negocio ha sido actualizado correctamente.'));
      this.closeSaleModal(true);
      await this.fetchSales(true);
    } catch (error) {
      this.$toast?.error(extractApiErrorMessage(error, messages.updateError || 'No fue posible actualizar el negocio.'));
    } finally {
      this.saleSubmitting = false;
    }
  },
  async handleEditSale(row) {
    const saleId = Number(row?.id || 0);
    if (saleId <= 0) {
      return;
    }

    try {
      const response = await readService.show(saleId, {}, { skipLoading: true });
      const detailPayload = extractPayload(response);

      if (!detailPayload || typeof detailPayload !== 'object') {
        this.$toast?.error(messages.loadEditError || 'No fue posible cargar el negocio para edición.');
        return;
      }

      this.saleModalMode = 'edit';
      this.editingSaleId = saleId;
      this.saleModalInitialData = detailPayload;
      this.showSaleEditModal = true;
    } catch (error) {
      this.$toast?.error(extractApiErrorMessage(error, messages.loadEditError || 'No fue posible cargar el negocio para edición.'));
    }
  },
  handleDeleteSale(row) {
    const saleId = Number(row?.id || 0);
    if (saleId <= 0) {
      return;
    }

    this.deletingSale = row;
    this.deletingSaleSource = 'table';
    this.isDeleteConfirmModalOpen = true;
  },
  handleDeleteSaleFromModal(sale) {
    const saleId = Number(sale?.id || this.editingSaleId || 0);
    if (saleId <= 0) {
      return;
    }

    this.deletingSale = {
      ...(sale && typeof sale === 'object' ? sale : {}),
      id: saleId,
      sale_name: String(
        sale?.sale_name
        || sale?.name
        || this.saleModalInitialData?.sale_name
        || this.saleModalInitialData?.name
        || ''
      ).trim(),
    };
    this.deletingSaleSource = 'modal';
    this.isDeleteConfirmModalOpen = true;
  },
  closeDeleteConfirmModal() {
    this.isDeleteConfirmModalOpen = false;
    this.deletingSale = null;
    this.deletingSaleSource = 'table';
  },
  async confirmDeleteSale() {
    const saleId = Number(this.deletingSale?.id || 0);
    if (saleId <= 0) {
      this.closeDeleteConfirmModal();
      return;
    }

    try {
      const response = await writeService.delete(saleId);
      const apiError = extractApiErrorMessage(response, messages.deleteError || 'No fue posible eliminar el negocio.');
      if (apiError) {
        this.$toast?.error(apiError);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, messages.deleteSuccess || 'El negocio ha sido eliminado correctamente.'));

      if (this.deletingSaleSource === 'modal') {
        this.closeSaleModal(true);
      }

      this.closeDeleteConfirmModal();
      await this.fetchSales(true);
    } catch (error) {
      this.$toast?.error(extractApiErrorMessage(error, messages.deleteError || 'No fue posible eliminar el negocio.'));
    }
  },
});
